import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CurrentTeamCard = ({ team, onLeaveTeam, onManageTeam, onViewWorkspace, currentUserId }) => {
  const [isLeaving, setIsLeaving] = useState(false);

  const handleLeaveTeam = async () => {
    setIsLeaving(true);
    try {
      await onLeaveTeam(team?.id);
    } finally {
      setIsLeaving(false);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      'Developer': 'bg-blue-100 text-blue-800',
      'Project Manager': 'bg-green-100 text-green-800',
      'Business Analyst': 'bg-purple-100 text-purple-800',
      'Product Designer': 'bg-orange-100 text-orange-800'
    };
    return colors?.[role] || 'bg-gray-100 text-gray-800';
  };

  const getCompletionPercentage = () => {
    const filledRoles = team?.members?.length;
    const totalRoles = 4; // Developer, PM, BA, Designer
    return Math.round((filledRoles / totalRoles) * 100);
  };

  const getMissingRoles = () => {
    const allRoles = ['Developer', 'Project Manager', 'Business Analyst', 'Product Designer'];
    const currentRoles = team?.members?.map(member => member?.role);
    return allRoles?.filter(role => !currentRoles?.includes(role));
  };

  const isTeamLeader = team?.leaderId === currentUserId;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">{team?.name}</h3>
          <p className="text-sm text-muted-foreground">{team?.description}</p>
        </div>
        {isTeamLeader && (
          <span className="px-2 py-1 text-xs font-medium bg-primary text-primary-foreground rounded-full">
            Team Leader
          </span>
        )}
      </div>
      {/* Team Completion Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Team Completion</span>
          <span className="text-sm text-muted-foreground">{getCompletionPercentage()}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${getCompletionPercentage()}%` }}
          />
        </div>
      </div>
      {/* Team Members */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Team Members ({team?.members?.length}/4)</h4>
        <div className="space-y-2">
          {team?.members?.map((member) => (
            <div key={member?.id} className="flex items-center space-x-3">
              <Image
                src={member?.avatar}
                alt={`${member?.name}'s profile`}
                className="w-8 h-8 rounded-full object-cover"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-foreground">{member?.name}</span>
                  {member?.id === team?.leaderId && (
                    <Icon name="Crown" size={12} className="text-yellow-500" />
                  )}
                </div>
                <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${getRoleColor(member?.role)}`}>
                  {member?.role}
                </span>
              </div>
              <div className={`w-2 h-2 rounded-full ${member?.isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
            </div>
          ))}
        </div>
      </div>
      {/* Missing Roles */}
      {getMissingRoles()?.length > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="AlertTriangle" size={16} className="text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">Missing Roles</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {getMissingRoles()?.map((role) => (
              <span key={role} className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                {role}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Problem Statement */}
      {team?.problemStatement && (
        <div className="mb-4 p-3 bg-muted rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="FileText" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Working On</span>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {team?.problemStatement}
          </p>
        </div>
      )}
      {/* Team Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-muted rounded-lg">
        <div className="text-center">
          <div className="text-lg font-semibold text-foreground">{team?.stats?.tasksCompleted}</div>
          <div className="text-xs text-muted-foreground">Tasks Done</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-foreground">{team?.stats?.meetingsHeld}</div>
          <div className="text-xs text-muted-foreground">Meetings</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-foreground">{team?.stats?.daysActive}</div>
          <div className="text-xs text-muted-foreground">Days Active</div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center space-x-2">
        <Button
          variant="default"
          size="sm"
          onClick={() => onViewWorkspace(team?.id)}
          iconName="Layout"
          iconPosition="left"
          iconSize={14}
          className="flex-1"
        >
          View Workspace
        </Button>
        
        {isTeamLeader && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onManageTeam(team?.id)}
            iconName="Settings"
            iconPosition="left"
            iconSize={14}
          >
            Manage
          </Button>
        )}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLeaveTeam}
          loading={isLeaving}
          iconName="LogOut"
          iconPosition="left"
          iconSize={14}
          className="text-destructive hover:text-destructive"
        >
          Leave
        </Button>
      </div>
    </div>
  );
};

export default CurrentTeamCard;